import { Box, ListItemButton, ListItemText, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Membership } from 'shared/types';
import { noop } from 'es-toolkit/compat';
import { ThermalHotspotTimeGuide } from 'features/mapping/components/LegendDrawerContent/ThermalHotspotTimeGuide';
import { FrpScaleGuide } from 'features/mapping/components/LegendDrawerContent/FrpScaleGuide';
import { UserStatusChip } from 'components/UserStatusChip';
import { LAYER_GROUPS, MapLayer } from 'features/mapping/mapping.constants';
import { layersDrawerChipSx } from '../LayersDrawer/layersDrawer.membershipChips';
import { SwitchControl } from '../SwitchControl';

type LayerGroupControlItemProps = {
  imageSrc: string;
  title: string;
  subtitle: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  mapLayer: MapLayer;
  disabled?: boolean;
  internalOnly?: boolean;
  experimental?: boolean;
  minMembership?: Membership;
};

const useStyles = makeStyles()(() => ({
  imgContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  img: {
    width: '100%',
    height: 'auto',
  },
}));

const isSatelliteMapLayer = (layer: MapLayer): boolean =>
  (LAYER_GROUPS.satelliteHotspots as readonly MapLayer[]).includes(layer);

export const LayerGroupControlItem = (
  props: LayerGroupControlItemProps,
): JSX.Element => {
  const {
    imageSrc,
    title,
    subtitle,
    checked,
    onChange,
    mapLayer,
    disabled,
    internalOnly,
    experimental,
    minMembership = 'public',
  } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const handleChange = (): void => {
    if (disabled) return;
    onChange(!checked);
  };

  const isSatelliteLayer = isSatelliteMapLayer(mapLayer);

  return (
    <ListItemButton divider onClick={handleChange}>
      <Stack direction="column" gap={1} pt={1}>
        <Box className={classes.imgContainer}>
          <img src={imageSrc} alt={title} className={classes.img} />
        </Box>
        <Stack flexDirection="row" alignItems="center">
          <ListItemText
            primary={
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.5,
                }}
              >
                {title}
                {minMembership !== 'public' && (
                  <UserStatusChip
                    type={minMembership === 'pro' ? 'pro' : 'members'}
                    sx={layersDrawerChipSx}
                  />
                )}
                {internalOnly && (
                  <UserStatusChip type="internal" sx={layersDrawerChipSx} />
                )}
              </Box>
            }
            secondary={disabled ? t('map.layers.disabled') : subtitle}
            slotProps={{
              primary: { variant: 'body1' },
              secondary: { variant: 'subtitle1' },
            }}
          />
          <SwitchControl
            disabled={Boolean(disabled)}
            checked={checked}
            onChange={noop}
            inputProps={{
              'aria-labelledby': t('map.accessibility.switchLayer'),
              'aria-label': `Toggle ${mapLayer}`,
            }}
          />
        </Stack>
        {isSatelliteLayer && (
          <Box sx={{ width: '100%', pb: 1 }}>
            {experimental ? (
              <FrpScaleGuide />
            ) : (
              <ThermalHotspotTimeGuide size="medium" />
            )}
          </Box>
        )}
      </Stack>
    </ListItemButton>
  );
};
